import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { csAdminApi } from '../services/api';
import { useSnackbar } from 'notistack';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Button,
  TextField,
  InputAdornment,
  Chip,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  Checkbox,
  Switch,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Alert,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Autocomplete,
} from '@mui/material';
import {
  SearchOutlined as SearchIcon,
  PersonAddOutlined as PersonAddIcon,
  MoreVertOutlined as MoreIcon,
  PersonOutlined as PersonIcon,
  EditOutlined as EditIcon,
  DeleteOutlined as DeleteIcon,
  LockOutlined as LockIcon,
  LockOpenOutlined as UnlockIcon,
  EmailOutlined as EmailIcon,
  SecurityOutlined as SecurityIcon,
  AdminPanelSettingsOutlined as AdminIcon,
  BusinessOutlined as CompanyIcon,
  CheckCircleOutlined as ActiveIcon,
  BlockOutlined as InactiveIcon,
  WarningAmberOutlined as WarningIcon,
  VisibilityOutlined as ViewIcon,
  FileDownloadOutlined as ExportIcon,
  PrintOutlined as PrintIcon,
  HistoryOutlined as AuditIcon,
} from '@mui/icons-material';
import {
  CS_ADMIN_ROLE_OPTIONS,
  CS_ADMIN_USERS_MODULE_ROLES,
  normalizeCsAdminRole,
  getCsAdminRoleLabel,
  canManageUserPermissions,
} from '../constants/csAdminRoles';
import { useAuth } from '../context/AuthContext';
import AccountCreatedDialog from '../components/users/AccountCreatedDialog';
import AuditLogComponent from '../components/common/AuditLogComponent';
import {
  shouldShowAccountSetupDialog,
  accountSetupSuccessMessage,
} from '../utils/accountSetupDisplay';

const ROLE_OPTIONS = CS_ADMIN_ROLE_OPTIONS;
const USERS_MODULE_ROLE_OPTIONS = CS_ADMIN_USERS_MODULE_ROLES;

const UsersPermissions = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [activeTab, setActiveTab] = useState(0);
  const [users, setUsers] = useState([]);
  const [availableCompanies, setAvailableCompanies] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Dialog states
  const [editUserOpen, setEditUserOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editPermissionsOpen, setEditPermissionsOpen] = useState(false);
  
  // Menu state
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuUser, setMenuUser] = useState(null);
  const [accountSetup, setAccountSetup] = useState(null);
  const [accountDialogOpen, setAccountDialogOpen] = useState(false);
  const [accountDialogTitle, setAccountDialogTitle] = useState('Account Created');
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditTablesMissing, setAuditTablesMissing] = useState(false);

  const loadAuditLogs = async () => {
    setAuditLoading(true);
    try {
      const res = await csAdminApi.getUserAuditLog({ limit: 100 });
      setAuditLogs(res.data?.data || []);
      setAuditTablesMissing(!!res.data?.tablesMissing);
    } catch (err) {
      console.error('Failed to load user audit log:', err);
      setAuditLogs([]);
    } finally {
      setAuditLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const res = await csAdminApi.getUsers();
      if (res.data?.data?.users) {
        setUsers(res.data.data.users);
      }
    } catch (err) {
      console.error('Failed to load users from API:', err);
    }
  };

  useEffect(() => {
    (async () => {
        await loadUsers();
        try {
          const companiesRes = await csAdminApi.getCompanies();
          const companies = (companiesRes.data?.data || []).map((c) => ({
            id: c.id || c.company_id,
            name: c.name || c.company_name,
          }));
          if (companies.length) {
            setAvailableCompanies(companies);
          }
        } catch (err) {
          console.error('Failed to load companies from API:', err);
        }
      })();
  }, []);

  useEffect(() => {
    if (activeTab === 1) {
      loadAuditLogs();
    }
  }, [activeTab]);

    const handleUpdateUser = async (updatedUser) => {
    if (updatedUser?.id) {
      try {
        await csAdminApi.updateUser(updatedUser.id, updatedUser);
        await loadUsers();
        if (activeTab === 1) await loadAuditLogs();
        return;
      } catch (err) {
        console.error('Failed to update user:', err);
      }
    }
    setUsers((prev) => prev.map((u) => (u.id === updatedUser.id ? updatedUser : u)));
  };

  const handleUserAction = async (user, action) => {
    handleMenuClose();
    if (!user?.id) return;
    try {
      if (action === 'reset-password') {
        const res = await csAdminApi.resetUserPassword(user.id);
        if (res.data?.data?.accountSetup) {
          const setup = res.data.data.accountSetup;
          if (shouldShowAccountSetupDialog(setup)) {
            setAccountDialogTitle('Password Reset');
            setAccountSetup(setup);
            setAccountDialogOpen(true);
          } else {
            enqueueSnackbar(accountSetupSuccessMessage(setup), { variant: 'success' });
          }
        } else {
          enqueueSnackbar('Password reset successfully', { variant: 'success' });
        }
      } else if (action === 'deactivate') {
        await csAdminApi.deleteUser(user.id);
      } else {
        // lock | unlock -> status
        await csAdminApi.setUserStatus(user.id, action === 'unlock' ? 'active' : 'inactive');
      }
      await loadUsers();
      if (activeTab === 1) await loadAuditLogs();
    } catch (err) {
      console.error(`Failed to ${action} user:`, err);
    }
  };

  // Stats
  const stats = {
    total: users.length,
    active: users.filter(u => u.status === 'active').length,
    inactive: users.filter(u => u.status === 'inactive').length,
    locked: users.filter(u => u.status === 'locked').length,
  };

  // Filter users
  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         user.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = roleFilter === 'all' || normalizeCsAdminRole(user.role) === roleFilter;
    const matchesStatus = statusFilter === 'all' || user.status === statusFilter;
    return matchesSearch && matchesRole && matchesStatus;
  });

  const handleMenuOpen = (event, user) => {
    setMenuAnchor(event.currentTarget);
    setMenuUser(user);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuUser(null);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setEditUserOpen(true);
    handleMenuClose();
  };

  const handleEditPermissions = (user) => {
    if (!canManageUserPermissions(currentUser?.role, user?.role)) {
      enqueueSnackbar('Only IT Admin can modify user permissions.', { variant: 'warning' });
      handleMenuClose();
      return;
    }
    setSelectedUser(user);
    setEditPermissionsOpen(true);
    handleMenuClose();
  };

  const getStatusChip = (status) => {
    const config = {
      active: { color: 'success', icon: <ActiveIcon sx={{ fontSize: 14 }} />, label: 'Active' },
      inactive: { color: 'default', icon: <InactiveIcon sx={{ fontSize: 14 }} />, label: 'Inactive' },
      suspended: { color: 'warning', icon: <WarningIcon sx={{ fontSize: 14 }} />, label: 'Suspended' },
      locked: { color: 'error', icon: <LockIcon sx={{ fontSize: 14 }} />, label: 'Locked' },
    };
    const { color, icon, label } = config[status] || config.inactive;
    return (
      <Chip 
        size="small" 
        color={color} 
        icon={icon}
        label={label}
        sx={{ height: 24 }}
      />
    );
  };

  const getRoleChip = (role, roleLabel) => {
    const colors = {
      GROUP_SECRETARY: 'error',
      COMPANY_SECRETARY: 'primary',
      AUDITOR: 'warning',
      VIEWER: 'default',
      IT_ADMIN: 'secondary',
      SUPER_ADMIN: 'error',
    };
    const normalizedRole = normalizeCsAdminRole(role);
    return (
      <Chip 
        size="small" 
        color={colors[normalizedRole] || 'default'}
        label={roleLabel || getCsAdminRoleLabel(role)}
        variant="outlined"
        sx={{ height: 24 }}
      />
    );
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Users & Permissions
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Manage users and access permissions
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Card variant="outlined">
            <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'primary.light', width: 48, height: 48 }}>
                  <PersonIcon sx={{ color: 'primary.main' }} />
                </Avatar>
                <Box>
                  <Typography variant="h5" fontWeight={600}>{stats.total}</Typography>
                  <Typography variant="body2" color="textSecondary">Total Users</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card variant="outlined">
            <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'success.light', width: 48, height: 48 }}>
                  <ActiveIcon sx={{ color: 'success.main' }} />
                </Avatar>
                <Box>
                  <Typography variant="h5" fontWeight={600}>{stats.active}</Typography>
                  <Typography variant="body2" color="textSecondary">Active</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card variant="outlined">
            <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'grey.200', width: 48, height: 48 }}>
                  <InactiveIcon sx={{ color: 'grey.600' }} />
                </Avatar>
                <Box>
                  <Typography variant="h5" fontWeight={600}>{stats.inactive}</Typography>
                  <Typography variant="body2" color="textSecondary">Inactive</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card variant="outlined">
            <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'error.light', width: 48, height: 48 }}>
                  <LockIcon sx={{ color: 'error.main' }} />
                </Avatar>
                <Box>
                  <Typography variant="h5" fontWeight={600}>{stats.locked}</Typography>
                  <Typography variant="body2" color="textSecondary">Locked</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs 
          value={activeTab} 
          onChange={(e, v) => setActiveTab(v)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Users" />
          <Tab label="Audit Log" />
        </Tabs>

        {/* Users Tab */}
        {activeTab === 0 && (
          <Box sx={{ p: 3 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              <strong>Company Secretaries</strong> (create, edit, assign companies) are managed from
              the Company Secretaries module — Secretarial Admin and IT Admin both have full access.
              This page is for Phase 1 admin users (Secretarial Admin and IT Admin) only;
              company secretary portal accounts are not listed here. Only{' '}
              <strong>IT Admin</strong> can change permission settings for users on this page.
            </Alert>
            {/* Filters */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
              <TextField
                placeholder="Search users..."
                size="small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 250 }}
              />
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Role</InputLabel>
                <Select
                  value={roleFilter}
                  label="Role"
                  onChange={(e) => setRoleFilter(e.target.value)}
                >
                  <MenuItem value="all">All Roles</MenuItem>
                  {USERS_MODULE_ROLE_OPTIONS.map(role => (
                    <MenuItem key={role.value} value={role.value}>{role.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                  <MenuItem value="locked">Locked</MenuItem>
                </Select>
              </FormControl>
              <Box sx={{ flexGrow: 1 }} />
              <Button
                variant="contained"
                startIcon={<PersonAddIcon />}
                onClick={() => navigate('/company-secretaries/create')}
              >
                Add Company Secretary
              </Button>
            </Box>

            {/* Users Table */}
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Assigned Companies</TableCell>
                    <TableCell>Last Login</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredUsers.map((user) => (
                    <TableRow key={user.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ bgcolor: 'primary.light', width: 40, height: 40 }}>
                            {user.name.split(' ').map(n => n[0]).join('')}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2">{user.name}</Typography>
                            <Typography variant="body2" color="textSecondary">
                              {user.email}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>{getRoleChip(user.role, user.roleLabel)}</TableCell>
                      <TableCell>{getStatusChip(user.status)}</TableCell>
                      <TableCell>
                        {user.assignedCompanies[0] === 'all' ? (
                          <Chip size="small" label="All Companies" color="primary" variant="outlined" />
                        ) : (
                          <Typography variant="body2">
                            {user.assignedCompanies.length} companies
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="textSecondary">
                          {user.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : 'Never'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={(e) => handleMenuOpen(e, user)}>
                          <MoreIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Audit Log Tab */}
        {activeTab === 1 && (
          <Box sx={{ p: 3 }}>
            {auditTablesMissing && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Audit log table is not installed. Run{' '}
                <code>backend/scripts/csadmin/20-user-audit-log.sql</code> on the database.
              </Alert>
            )}
            {auditLoading ? (
              <Typography variant="body2" color="textSecondary">
                Loading audit log…
              </Typography>
            ) : (
              <AuditLogComponent
                auditLogs={auditLogs}
                title="User Management Audit Log"
              />
            )}
          </Box>
        )}
      </Paper>

      {/* User Actions Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleEditUser(menuUser)}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          Edit User
        </MenuItem>
        {canManageUserPermissions(currentUser?.role, menuUser?.role) && (
          <MenuItem onClick={() => handleEditPermissions(menuUser)}>
            <ListItemIcon><SecurityIcon fontSize="small" /></ListItemIcon>
            Edit Permissions
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={() => handleUserAction(menuUser, 'reset-password')}>
          <ListItemIcon><EmailIcon fontSize="small" /></ListItemIcon>
          Reset Password
        </MenuItem>
        {menuUser?.status === 'locked' || menuUser?.status === 'inactive' ? (
          <MenuItem onClick={() => handleUserAction(menuUser, 'unlock')}>
            <ListItemIcon><UnlockIcon fontSize="small" /></ListItemIcon>
            Unlock Account
          </MenuItem>
        ) : (
          <MenuItem onClick={() => handleUserAction(menuUser, 'lock')}>
            <ListItemIcon><LockIcon fontSize="small" /></ListItemIcon>
            Lock Account
          </MenuItem>
        )}
        <Divider />
        <MenuItem sx={{ color: 'error.main' }} onClick={() => handleUserAction(menuUser, 'deactivate')}>
          <ListItemIcon><DeleteIcon fontSize="small" sx={{ color: 'error.main' }} /></ListItemIcon>
          Deactivate User
        </MenuItem>
      </Menu>

      {/* Edit User Dialog */}
      <EditUserDialog
        open={editUserOpen}
        user={selectedUser}
        availableCompanies={availableCompanies}
        onClose={() => {
          setEditUserOpen(false);
          setSelectedUser(null);
        }}
        onSave={async (updatedUser) => {
          await handleUpdateUser(updatedUser);
          setEditUserOpen(false);
          setSelectedUser(null);
        }}
      />

      {/* Edit Permissions Dialog */}
      <EditPermissionsDialog
        open={editPermissionsOpen}
        user={selectedUser}
        onClose={() => {
          setEditPermissionsOpen(false);
          setSelectedUser(null);
        }}
        onSave={async ({ useCustom, matrix }) => {
          try {
            if (selectedUser?.id) {
              await csAdminApi.updateUserPermissions(selectedUser.id, { useCustom, matrix });
              enqueueSnackbar('Permissions saved', { variant: 'success' });
              if (activeTab === 1) await loadAuditLogs();
            }
          } catch (err) {
            console.error('Save permissions failed:', err);
            enqueueSnackbar('Failed to save permissions', { variant: 'error' });
            return;
          }
          setEditPermissionsOpen(false);
          setSelectedUser(null);
        }}
      />
      <AccountCreatedDialog
        open={accountDialogOpen}
        onClose={() => {
          setAccountDialogOpen(false);
          setAccountSetup(null);
        }}
        title={accountDialogTitle}
        subtitle="Save these credentials now. They will not be shown again."
        accountSetup={accountSetup}
      />
    </Box>
  );
};

// Edit User Dialog Component
const EditUserDialog = ({ open, user, onClose, onSave, availableCompanies = [] }) => {
  const [formData, setFormData] = useState({});

  useEffect(() => {
    if (user) {
      setFormData({ ...user });
    }
  }, [user]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (!user) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit User - {user.name}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Full Name"
                value={formData.name || ''}
                onChange={(e) => handleChange('name', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email || ''}
                onChange={(e) => handleChange('email', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                value={formData.phone || ''}
                onChange={(e) => handleChange('phone', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status || 'active'}
                  label="Status"
                  onChange={(e) => handleChange('status', e.target.value)}
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                  <MenuItem value="suspended">Suspended</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  value={formData.role || ''}
                  label="Role"
                  onChange={(e) => {
                    handleChange('role', e.target.value);
                    handleChange('roleLabel', ROLE_OPTIONS.find(r => r.value === e.target.value)?.label);
                  }}
                >
                  {USERS_MODULE_ROLE_OPTIONS.map(role => (
                    <MenuItem key={role.value} value={role.value}>{role.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {normalizeCsAdminRole(formData.role) !== 'GROUP_SECRETARY'
              && normalizeCsAdminRole(formData.role) !== 'IT_ADMIN' && (
              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  options={availableCompanies}
                  getOptionLabel={(option) => option.name}
                  value={availableCompanies.filter(c => (formData.assignedCompanies || []).includes(c.id))}
                  onChange={(e, newValue) => handleChange('assignedCompanies', newValue.map(c => c.id))}
                  renderInput={(params) => (
                    <TextField {...params} label="Assigned Companies" />
                  )}
                />
              </Grid>
            )}
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => onSave(formData)}>
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Edit Permissions Dialog Component
const EditPermissionsDialog = ({ open, user, onClose, onSave }) => {
  const [useCustom, setUseCustom] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [permissions, setPermissions] = useState({
    companies: { view: true, edit: true, create: false, delete: false, export: true },
    directors: { view: true, edit: true, create: true, delete: false, export: true },
    committees: { view: true, edit: true, create: true, delete: true, export: true },
    orgStructure: { view: true, edit: false, exportPng: true, exportSvg: true, print: true },
    expiryAlerts: { view: true, configure: false, export: true },
    settings: { view: true, edit: true },
  });

  useEffect(() => {
    if (!open || !user) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await csAdminApi.getUserPermissions(user.id);
        const data = res.data?.data;
        if (cancelled || !data) return;
        setUseCustom(!!data.useCustom);
        setPermissions(data.useCustom ? (data.matrix || data.effective) : (data.effective || data.matrix));
      } catch (err) {
        console.error('Load user permissions:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [open, user]);

  const modules = [
    { key: 'companies', label: 'Companies', actions: ['view', 'edit', 'create', 'delete', 'export'] },
    { key: 'directors', label: 'Directors', actions: ['view', 'edit', 'create', 'delete', 'export'] },
    { key: 'committees', label: 'Committees', actions: ['view', 'edit', 'create', 'delete', 'export'] },
    { key: 'orgStructure', label: 'Org Structure', actions: ['view', 'edit', 'exportPng', 'exportSvg', 'print'] },
    { key: 'expiryAlerts', label: 'Expiry Alerts', actions: ['view', 'configure', 'export'] },
    { key: 'settings', label: 'Settings', actions: ['view', 'edit'] },
  ];

  const actionLabels = {
    view: 'View',
    edit: 'Edit',
    create: 'Create',
    delete: 'Delete',
    export: 'Export',
    exportPng: 'PNG',
    exportSvg: 'SVG',
    print: 'Print',
    configure: 'Configure',
  };

  const handlePermissionChange = (module, action, value) => {
    setPermissions(prev => ({
      ...prev,
      [module]: { ...prev[module], [action]: value }
    }));
  };

  if (!user) return null;

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({ useCustom, matrix: permissions });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Edit Permissions - {user.name}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <Typography color="textSecondary">Loading permissions…</Typography>
            </Box>
          ) : (
            <>
          <Alert severity="info" sx={{ mb: 3 }}>
            Using default permissions for: <strong>{user.roleLabel || user.role}</strong>
          </Alert>

          <FormControlLabel
            control={
              <Switch 
                checked={useCustom}
                onChange={(e) => setUseCustom(e.target.checked)}
              />
            }
            label="Use custom permissions (override role defaults)"
            sx={{ mb: 3 }}
          />

          <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 2 }}>
            MODULE PERMISSIONS
          </Typography>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Module</TableCell>
                  <TableCell align="center">View</TableCell>
                  <TableCell align="center">Edit</TableCell>
                  <TableCell align="center">Create</TableCell>
                  <TableCell align="center">Delete</TableCell>
                  <TableCell align="center">Export/Print</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {modules.map((module) => (
                  <TableRow key={module.key}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {module.label}
                      </Typography>
                    </TableCell>
                    {['view', 'edit', 'create', 'delete'].map(action => (
                      <TableCell key={action} align="center">
                        {module.actions.includes(action) ? (
                          <Checkbox
                            size="small"
                            checked={permissions[module.key]?.[action] || false}
                            onChange={(e) => handlePermissionChange(module.key, action, e.target.checked)}
                            disabled={!useCustom}
                          />
                        ) : (
                          <Typography variant="body2" color="textSecondary">-</Typography>
                        )}
                      </TableCell>
                    ))}
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                        {module.actions.filter(a => ['export', 'exportPng', 'exportSvg', 'print', 'configure'].includes(a)).map(action => (
                          <Tooltip key={action} title={actionLabels[action]}>
                            <Checkbox
                              size="small"
                              checked={permissions[module.key]?.[action] || false}
                              onChange={(e) => handlePermissionChange(module.key, action, e.target.checked)}
                              disabled={!useCustom}
                            />
                          </Tooltip>
                        ))}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 3, mb: 2 }}>
            FEATURE PERMISSIONS
          </Typography>

          <Grid container spacing={1}>
            {[
              { key: 'exportExcel', label: 'Export to Excel' },
              { key: 'exportPdf', label: 'Export to PDF' },
              { key: 'print', label: 'Print' },
              { key: 'bulkActions', label: 'Bulk Actions' },
              { key: 'downloadDocs', label: 'Download Documents' },
              { key: 'sendNotifications', label: 'Send Notifications' },
              { key: 'viewAudit', label: 'View Audit Log' },
            ].map(feature => (
              <Grid item xs={6} sm={4} key={feature.key}>
                <FormControlLabel
                  control={<Checkbox size="small" defaultChecked disabled={!useCustom} />}
                  label={<Typography variant="body2">{feature.label}</Typography>}
                />
              </Grid>
            ))}
          </Grid>
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={loading || saving}>
          Save Permissions
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UsersPermissions;
